import _ from 'lodash';
import store from '@/store/index';
import { translateToEn, translation } from '@/plugins/i18n';
import { checkMRSChange } from '@/utils/validator';
import { pageConfig } from '@/common/service/page-config';
import {
  BASE_PLAN_SUB_TYPES,
  MRS_STATUS_TYPE_MAP,
  planTypes,
  updateTypeMap,
  ZOOM_SASS_ADDITIONAL_CATEGORIES,
} from '@/common/constants';
import MessageBox from '@zoom/zoom-ui/src/components/message-box/message-box';
import { isZoomPhone } from '@/common/service/computers/plans/pbx';
import { dateTime, dateTimeFormat } from '@/utils/dateformat';
import { validateEditBulkLicense } from '@/apis';
import $tracking from '@/aspects/trackings';
import { get } from 'lodash';
import { isEmptyVal } from '@/utils';
import { couponFetchStatus } from '@/views/config/coupon/helper/constant';
import { isBizPlusContext, isEduSchCmpPlusContext } from '@/plans/ZOOM_MEETING/common/utils';
import { checkBizPlusDownsell, checkBizPlusAssignInfo, checkBizPlusToBizAssignInfo } from '@/plans/ZOOM_ONE_BUNDLE/utils';
import moment from 'moment';
import { span } from '@/views/config/shopping-cart/next.vue'
import { checkZpUpOrDown } from '@/plans/ZOOM_PHONE/utils';
import { plansof } from '@/utils/chain';
import { checkEduPlus2EduAssignInfo, checkEduPlusAssignInfo, checkEduPlusDownSell } from '@/plans/ZONE_EDU_PLUS/utils';
import { getBasePlanInfo } from '@/common/product';

export const checkOneMonthPlanDownSellForEdit = async () => {
  const getters = store.getters;

  // exclude campaign link for check downsell
  if (getters.fromCampaignLink) return Promise.resolve();

  const oldPlan = getters.currentEditPlan;
  const editPlanType = pageConfig?.editConfig?.planType;
  const scopePlanTypeList = [planTypes.WEBINAR, planTypes.LARGE_MEETING];
  const oldPlanIsOneMonth = oldPlan?.isOneMonth;

  if (scopePlanTypeList.includes(editPlanType) && oldPlanIsOneMonth) {
    const nextPlan = _.get(getters.getTypeGroupSelectedPlans, `${editPlanType}[0]`);

    const status = checkMRSChange({ oldPlan, nextPlan });

    if (status === MRS_STATUS_TYPE_MAP.CHANGE_FUTURE) {
      MessageBox.alert(
        translation('billing.error.one_month_error_tips'),
        translation('billing.buyItNow.please_note'),
      );

      return Promise.reject();
    }
  }

  return Promise.resolve();
};

const confirmModal = (planExpireTime) => {
  const editPlanType = pageConfig?.editConfig?.planType;
  const getters = store.getters;
  let message = translation('billing.error.downsell_desc_tips_common', [planExpireTime]);
  // zoom sass addon
  if (ZOOM_SASS_ADDITIONAL_CATEGORIES.includes(editPlanType)) {
    const { quantity = 0 } = get(getters.selectedPlanListPayLoad, '[0]', {});
    const { assignLicenseQuantity = 0, planExpireTime: expireTime } = get(getters.assignInfo, pageConfig.oldPlanCode, {});
    const planType = {
      [planTypes.ADDITIONAL_HOST]: translation('billing.events.addon.hosts'),
      [planTypes.ADDITIONAL_CO_EDITOR]: translation('billing.events.addon.co_editors'),
    }[editPlanType];

    if (!isEmptyVal(quantity) && quantity < assignLicenseQuantity) {
      const needAssignQuantity = assignLicenseQuantity - quantity;
      message = translation('billing.error.downgrade_pre_confirm_for_ze_addon', [
        needAssignQuantity,
        planType,
        dateTimeFormat(expireTime),
      ]);
    }
  }
  return MessageBox.confirm(message, translation('billing.error.downsell_desc_title'), {
    confirmButtonText: translation('common.btn_yes'),
    cancelButtonText: translation('common.btn_no'),
  });
};

const showDownSellConfirmModal = (planExpireTime = '') => {
  const productName = translation(store.getters.currentEditPlan?.planNameKey);
  const trackingProductName = translateToEn(store.getters.currentEditPlan?.planNameKey);
  if ([BASE_PLAN_SUB_TYPES.ZONE_EDU_PLUS, BASE_PLAN_SUB_TYPES.ZONE_EDU_SCH_CMP].includes(store.getters.currentEditPlan?.zoomMeetingCategory)) {
    $tracking.buyInteraction("remove users downgrade", `${trackingProductName} - downgrade your plan - modal impression`);
  } else {
    $tracking.buyInteraction("remove users downgrade", `${productName} - modal impression`);
  }
  return confirmModal(planExpireTime).then(() => {
    // for downgrade case
    const { assignInfo, currentEditPlan, getTypeGroupSelectedPlans } = store.getters;

    const { assignLicenseQuantity = 0 } = _.get(assignInfo, [currentEditPlan?.plan_code], {});
    let targetType = currentEditPlan?.type;
    if (store.getters.migratePlanState.is.bizplus2proplus) {
      targetType = planTypes.ZONE_PRO_PLUS;
    }
    const currentQuantity = _.get(getTypeGroupSelectedPlans[targetType], '0.cartInfo.selected_number', 0);
    let unAssignLicenseQuantity = assignLicenseQuantity - currentQuantity;

    if (isBizPlusContext && !store.getters.migratePlanState.is.bizplus2proplus) {
      if (store.getters.migratePlanState.is.bundle2biz) {
        unAssignLicenseQuantity = checkBizPlusToBizAssignInfo();
      } else if (store.getters.migratePlanState.is.upgrade) {
        // can not reduce licenses below assinged lincese
        unAssignLicenseQuantity = 0;
      } else {
        unAssignLicenseQuantity = checkBizPlusAssignInfo();
      }
    }

    if (isEduSchCmpPlusContext) {
      if (store.getters.migratePlanState.is.escp2esc) {
        unAssignLicenseQuantity = checkEduPlus2EduAssignInfo();
      } else if (store.getters.migratePlanState.is.upgrade) {
        unAssignLicenseQuantity = 0;
      } else {
        unAssignLicenseQuantity = checkEduPlusAssignInfo();
      }
    }

    store.commit('savePersistState', {
      downgradeState: {
        updateType: updateTypeMap.downgrade,
        planExpireTime,
        unAssignLicenseQuantity,
      },
    });
    if ([BASE_PLAN_SUB_TYPES.ZONE_EDU_PLUS, BASE_PLAN_SUB_TYPES.ZONE_EDU_SCH_CMP].includes(store.getters.currentEditPlan?.zoomMeetingCategory)) {
      $tracking.buyInteraction("remove users downgrade", `${trackingProductName} - downgrade your plan - modal yes`);
    } else {
      $tracking.buyInteraction("remove users downgrade", `${productName} - modal yes`);
    }

    return Promise.resolve();
  }).catch(() => {
    if ([BASE_PLAN_SUB_TYPES.ZONE_EDU_PLUS, BASE_PLAN_SUB_TYPES.ZONE_EDU_SCH_CMP].includes(store.getters.currentEditPlan?.zoomMeetingCategory)) {
      $tracking.buyInteraction("remove users downgrade", `${trackingProductName} - downgrade your plan - modal no`)
    } else {
      $tracking.buyInteraction("remove users downgrade", `${productName} - modal no`)
    }
    return Promise.reject()
  });
};
/**
 *
 * @type {{DOWN: number, UP: number, NO_CHANGE: number}}
 */
export const PLAN_CHANGE_TYPE = {
  UP: 1,
  DOWN: -1,
  NO_CHANGE: 0,
};

const getDownSellEffectiveDateFromBackend = (oldPlanCode) => {
  const nextChargeDate = store.getters.nextChargeDate;
  const res = nextChargeDate?.pendingPlanList.find((p) => p.oldPlanCode === oldPlanCode)?.futureEffectiveDate ?? null;
  return res ? dateTimeFormat(res) : null;
};

export const checkPlanDownSellForEdit = async (isSilent = false) => {
  const getters = store.getters;

  // reset persist state
  store.commit('savePersistState', { downgradeState: {} });

  if (isSilent) {
    // for prorated case, as long as the customer from the partner and with coupon promotion, will do not check downsell
    // if the partner not combine with zoom coupon promotion, can be as regular case for downsell check
    if ((getters.partnerRefer?.jointsaleCode || getters.partnerRefer?.referralCode) && getters?.partnerRefer?.couponCode) return Promise.resolve();
  } else {
    // for partnership with jointsale/referral mode, do not check downsell, BE use replace subscription, so downsell host will immediately effective
    if (getters.partnerRefer?.jointsaleCode || getters.partnerRefer?.referralCode) return Promise.resolve();
  }
  // exclude campaign link for check downsell
  if (getters.fromCampaignLink && getters.verifyCouponStatus === couponFetchStatus.successfully) return Promise.resolve();
  // exclude biz upgrade to biz plus
  if (getters.migratePlanState.is.upgrade) return Promise.resolve();

  ////compute from backend
  const oldPlan = getters.currentEditPlan;
  let downSellFutureEffectiveDateFromBE = getDownSellEffectiveDateFromBackend(oldPlan?.plan_code)

  // biz plus downgrade to biz
  if (getters.migratePlanState.is.bundle2biz) {
    const { boughtBizPlusPlan } = getters.plansofBizBundle;
    const bizPlusExpireTime = dateTimeFormat(boughtBizPlusPlan?.[0]?.cartInfo?.planExpireTime);
    if (bizPlusExpireTime) {
      return isSilent ? Promise.reject() : showDownSellConfirmModal(downSellFutureEffectiveDateFromBE || bizPlusExpireTime);
    }
    return Promise.resolve();
  }
  // edu school plus => edu school
  if (getters.migratePlanState.is.escp2esc) {
    const boughtEduPlusPlan = plansof(getters.boughtPlans).type(planTypes.ZONE_EDU_PLUS).get();
    const eduPlusExpireTime = dateTimeFormat(boughtEduPlusPlan?.[0]?.cartInfo?.planExpireTime);
    if (eduPlusExpireTime) {
      return isSilent ? Promise.reject() : showDownSellConfirmModal(downSellFutureEffectiveDateFromBE || eduPlusExpireTime);
    }
    return Promise.resolve();
  }
  if (getters.migratePlanState.is.biz2proplus) return Promise.resolve();
  if (getters.migratePlanState.is.bizplus2proplus && getters.currentEditPlan) {
    const bizPlusExpireTime = dateTimeFormat(getters.currentEditPlan.cartInfo?.planExpireTime);
    if (bizPlusExpireTime) {
      return isSilent ? Promise.reject() : showDownSellConfirmModal(downSellFutureEffectiveDateFromBE || bizPlusExpireTime);
    }
    return Promise.resolve();
  }

  const editPlanType = pageConfig?.editConfig?.planType;
  const currentPlans = getters.getTypeGroupSelectedPlans[editPlanType] || [];
  const nextPlan = currentPlans?.[0] || {};
  const oldPlanCode = _.get(oldPlan, 'plan_code', '');
  const oldPlanLicense = _.get(oldPlan, 'cartInfo.selected_number', 0);
  let planExpireTime = dateTimeFormat(oldPlan?.cartInfo?.planExpireTime);
  //compute from frontend
  if (getters.shouldUseTermEndDate) {
    const termEndDate = dateTimeFormat(getters.basicConfig?.termEndDate);
    if (_.get(oldPlan, 'cartInfo.cancelled', false)) {
      planExpireTime = dateTimeFormat(_.get(oldPlan, 'cartInfo.originalPlanExpireTime')) || termEndDate
    } else if (getters.basicConfig?.evergreen === true && planExpireTime && termEndDate) {
      planExpireTime = _.sortBy([planExpireTime, termEndDate], d => new Date(d))[0];
    } else {
      planExpireTime = termEndDate
    }
  } else if (pageConfig?.editConfig?.planType === planTypes.CMR) {
    planExpireTime = moment(oldPlan?.cartInfo?.planStartTime || new Date())
      .add(...span[oldPlan?.billing_cycle])
      .format(dateTime);
  }

  if (isZoomPhone(editPlanType)) {
    const status = checkZpUpOrDown();
    store.commit('savePersistState', { downgradeState: { status } });
    return Promise.resolve();
  }

  if (currentPlans.length <= 0) {
    // No plan selected; Need a prompt
    return Promise.reject();
  }
  if (currentPlans.length > 1 && (!isBizPlusContext && !isEduSchCmpPlusContext)) {
    return Promise.resolve();
  }

  const nextPlanCode = _.get(nextPlan, 'plan_code', '');
  const nextPlanLicense = _.get(nextPlan, 'cartInfo.selected_number', 0);
  const isBulkLicenseAccount = getters.basicConfig?.isBulkLicenseAccount;

  store.commit('saveCurrentEditPlan', oldPlan);

  // for special case (pro2biz, biz2pro)
  if (editPlanType === planTypes.ZOOM_MEETING) {
    if(oldPlan?.category !== nextPlan?.category) {
      if (nextPlan?.category === BASE_PLAN_SUB_TYPES.BIZ) {
        // pro upgrade to biz case (do not need to downsell check)
        return Promise.resolve();
      } else if (nextPlan.category === BASE_PLAN_SUB_TYPES.PRO) {
        // this is biz downgrade to pro case, this is downgrade case
        return isSilent ? Promise.reject() : showDownSellConfirmModal(downSellFutureEffectiveDateFromBE || planExpireTime);
      }
    }
  }

  const { bizPlusExpireTime } = checkBizPlusDownsell();
  const { eduPlusExpireTime } = checkEduPlusDownSell();

  if (
    (oldPlanCode &&
    oldPlanCode === nextPlanCode &&
    nextPlanLicense < oldPlanLicense) || !!bizPlusExpireTime || !!eduPlusExpireTime
  ) {
    // for bulk license downSell check
    if (isBulkLicenseAccount) {
      try {
        await validateEditBulkLicense({
          planCode: oldPlanCode,
          oldQty: oldPlanLicense,
          newQty: nextPlanLicense,
        });

        return Promise.resolve();
      } catch (e) {
        isSilent ? null : MessageBox.alert(
          e.message,
          translation('billing.buyItNow.cant_continue'),
          {
            dangerouslyUseHTMLString: true,
          },
        );

        return Promise.reject();
      }
    } else {
      return isSilent ? Promise.reject() : showDownSellConfirmModal( downSellFutureEffectiveDateFromBE || bizPlusExpireTime || eduPlusExpireTime || planExpireTime);
    }
  }

  if (isBizPlusContext || isEduSchCmpPlusContext) {
    const getters = store.getters;
    const { boughtBizPlusPlan, selectedBizPlusPlan } = getters.plansofBizBundle;
    let boughtPlans = [...boughtBizPlusPlan];
    let selectedPlans = [...selectedBizPlusPlan];

    if (isEduSchCmpPlusContext) {
      boughtPlans = [...(plansof(getters.boughtPlans).type(planTypes.ZONE_EDU_PLUS).get())];
      selectedPlans = [...(plansof(getters.getSelectedPlans).type(planTypes.ZONE_EDU_PLUS).get())];
    }

    for (let i = 0; i < boughtPlans.length; i++) {
      const p = boughtPlans[i];
      const target = selectedPlans.find(item => item.plan_code === p.plan_code);
      if (target) {
        let planExpireTime = dateTimeFormat(p?.cartInfo?.planExpireTime);
        if (getters.basicConfig?.term !== p?.cycleValue) {
          const termEndDate = dateTimeFormat(getters.basicConfig?.termEndDate);
          if (getters.basicConfig?.evergreen === true && planExpireTime && termEndDate) {
            planExpireTime = _.sortBy([planExpireTime, termEndDate], d => new Date(d))[0];
          } else {
            planExpireTime = termEndDate
          }
        }
        const status = checkMRSChange({ oldPlan: p, nextPlan: target });
        if (status === MRS_STATUS_TYPE_MAP.CHANGE_FUTURE) {
          downSellFutureEffectiveDateFromBE = getDownSellEffectiveDateFromBackend(p.plan_code)
          return isSilent ? Promise.reject() : showDownSellConfirmModal(downSellFutureEffectiveDateFromBE || planExpireTime);
        }
      }
    }
    return Promise.resolve();
  }

  const status = checkMRSChange({ oldPlan, nextPlan });

  if (status === MRS_STATUS_TYPE_MAP.CHANGE_FUTURE) {
    return isSilent ? Promise.reject() : showDownSellConfirmModal(downSellFutureEffectiveDateFromBE || planExpireTime);
  }

  return Promise.resolve();
};

export function checkDownSellForDowngrade(getters) {
  const { planType: boughtBaseType, plans } = getBasePlanInfo(getters.boughtPlans);
  const boughtBasePlan = plans[0];
  if (!boughtBasePlan) return Promise.resolve();
  if (
    planTypes.ZONE_PRO_PLUS === pageConfig.editConfig?.planType &&
    planTypes.ZOOM_ONE_BUNDLE === boughtBaseType
  ) {
    const hasLargeMeeting = _.some(getters.getSelectedPlans, (plan) => plan.type === planTypes.LARGE_MEETING)
    const bizPlusExpireTime = dateTimeFormat(boughtBasePlan.cartInfo?.planExpireTime);
    if (bizPlusExpireTime && !hasLargeMeeting) {
      return showDownSellConfirmModal(bizPlusExpireTime);
    }
    return Promise.resolve();
  }
}
